import { inject, observer } from "mobx-react";
import toast from "react-hot-toast";
import React from "react";
import { useTranslation } from "react-i18next";
import { AuthStore, MeetingStore, UserStore } from "src/stores";
import { EditData } from ".";
import { theme } from "src/config";
import { FileView } from "./fileView";
import { AttendanceView } from "./attendanceView";
import { AgendaView } from "./agendaView";
import { User, Meeting } from "src/models";
import { Grid, Cell, MultiSelect, Input, Option } from "src/components";
import { MeetingType, UserRole } from "src/types";
import { parse } from "date-fns";
import { HistoryView } from "../../../views/log/historyView";
import { SummaryView } from "../../../views/log/summaryView";

export interface IEditMeetingProps {
  authStore?:    AuthStore;
  userStore?:    UserStore;
  meetingStore?: MeetingStore;
}

export const EditMeeting = inject("authStore", "userStore", "meetingStore")(observer((props: IEditMeetingProps): JSX.Element => {
  const { meetingStore, userStore, authStore } = props;
  const { t, i18n } = useTranslation();
  const ref = React.useRef<{ setActiveView: (view_key: string) => void }>();
  const m: Meeting = meetingStore.getActive;
  const projectLeader = [UserRole.Leader, UserRole.Owner].includes(authStore.getCurUser?.getRole(m.room_id));
  const isCreator = m.created_by == authStore.getCurUserID;

  if (!m) return null;
  const isMeetingLeader = m.getLeader?.id === authStore.getCurUserID;
  const editableForCurrentUser = m.canEdit && (projectLeader || isMeetingLeader || isCreator);
  
  return (
    <EditData
      hideTags
      ref       = {ref}
      descLabel = {t("instruction_plural")}
      data      = {m}
      canEdit   = {editableForCurrentUser}
      onClose   = {async () => {
        const valid = await m.validate();
        if(!valid.length || !editableForCurrentUser) {
          meetingStore.setActive(null);
        } else {
          toast.error(`${t("failed_to_save_item_missing_properties", {type: t('meeting').toLowerCase()})} ${(valid||[]).map((k:string) => t(k, {ns: 'prop'})).join(', ')} `);
        }
      }}
      body      = {
        <Grid rowGap={"2rem"}>
          <Cell width={12} key='conference_call'>
            <MultiSelect
              singleSelect
              placeholder = {t("select_conference_call")}
              label       = {t("conference_call")}
              selected    = {m.canEdit ? [{ value: m.type, label: t(m.type) }] : []}
              onChange    = {(option: Option) => m.set("type", option?.value, true)}
              disabled    = {!m.canEdit}
              options     = {Object.values(MeetingType).map((type) => {
                return { label: t(type), value: type };
              })}
            />
          </Cell>
            {m.type == 'zoom' && (
              <Cell width={12} key='password'>
                <Input
                  type="text"
                  value={m.password}
                  placeholder={t("password")}
                  onChange={(val) => m.set("password", val)}
                  onBlur={(event) => m.save({ password: m.password })}
                  label={t("password")}
                  icons={[
                    {
                      iconId: "eye"
                    }
                  ]}
              />
              </Cell>
            )}
          <Cell width={12} key='category'>
            <MultiSelect
              singleSelect
              placeholder = {t("select_category")}
              label       = {t("category")}
              options     = {Meeting.getCategories}
              selected    = {m.canEdit ? [{ value: m.category, label: t(m.category) }] : []}
              disabled    = {!m.canEdit}
              onChange    = {(option: Option) => m.set("category", option?.value, true)}
            />
          </Cell>

          <Cell width={12} key='url'>
            <Input
              name        = "url"
              type        = "url"
              value       = {m.url}
              placeholder = {"https://"}
              helperText  = {t("meeting_url_input_tooltip")}
              onChange    = {(val) => m.set("url", val)}
              onBlur      = {(event) => m.save({ url: m.url })}
              label       = {t("external_link")}
              icons       = {[{iconId: "connect"}]}
            />
          </Cell>

          <Cell width={12} key='location'>
            <Input
              type        = "text"
              value       = {m.location}
              placeholder = {t("enter_location")}
              onChange    = {(val) => m.set("location", val)}
              onBlur      = {(event) => m.save({ location: m.location })}
              label       = {t("location")}
              icons       = {[{iconId: "location"}]}
            />
          </Cell>
        </Grid>
      }
      subViews={[
        {
          iconId: "file",
          key:    "file",
          title:  t("file_plural"),
          body:   <FileView item={m} />,
          count:  m.total_files
        },{
          iconId: "group",
          key:    "attendance",
          title:  t("attendance"),
          body:   <AttendanceView item={m} />
        },{
          iconId: "history",
          key: "history",
          title: t("action_plural"),
          body: <HistoryView item={m} />
        },
        // {
        //   iconId: "analytics",
        //   key:    "summary",
        //   title:  t("summary"),
        //   body:   <SummaryView item={m} />
        // },{
        //   iconId: "menu",
        //   key:    "agenda",
        //   title:  t("agenda"),
        //   body:   <AgendaView item={m} />
        // }
      ]}
      sections={[
        {
          title:   t("attribute_plural"),
          entries: [
            {
              type:     "date",
              title:    t("date"),
              value:    m.date,
              disabled: !m.canEdit,
              onChange: (value) => {
                m.set("date", new Date(value), true);
                if (m.start) {
                  const date    = new Date(m.start);
                  const hours   = date.getHours();
                  const minutes = date.getMinutes();
                  const d       = parse(`${hours}:${minutes}`, "HH:mm", new Date(m.date));
                  m.set("date", d, true);
                  m.set("start", d, true);
                }
              }
            },{
              type:     "time",
              title:    t("time"),
              value:    m.start,
              disabled: !m.canEdit,
              onChange: (value) => {
                const d = parse(value, "HH:mm", new Date(m.date))
                m.set("date", d, true);
                m.set("start", d, true);
              }
            },{
              type:        "number",
              title:       t("duration_in_hours"),
              value:       m.duration,
              disabled:    !m.canEdit,
              onChange:    (value) => m.set("duration", value || 0, true),
              placeholder: "0",
              // iconId:      "duration"
            },{
              type:        "number",
              title:       t("reminder_in_minutes"),
              value:       m.reminder_minutes,
              disabled:    !m.canEdit,
              placeholder: "0",
              // iconId:      "duration",
              onChange:    (value) => m.set("reminder_minutes", value || 0, true)
            }
          ]
        },{
          title:   t("participant_plural"),
          content: t("meeting_invitation_note"),
          entries: [
            {
              type:     "user",
              title:    t("leader"),
              value:    m.getLeader,
              options:  meetingStore.getAvailableParticipants,
              disabled: !m.canEdit,
              onChange: (user: User) => m.set("leader_id", user?.id || null, true)
            },{
              type:         "users",
              title:        t("participant_plural"),
              value:        userStore.getLinks,
              options:      meetingStore.getAvailableParticipants,
              disabled:     !m.canEdit,
              isLoading:    userStore.links.getStatus,
              showLoadMore: false,
              onAdd:        (user: User) => userStore.addLink(user.id, "participant"),
              onRemove:     (user: User) => userStore.removeLink(user.id, "participant"),
              onLoad:       () => {}
            }
          ]
        },{
          title:   t("option_plural"),
          entries: [
            {
              type:     "action",
              title:    t("start_meeting"),
              iconId:   "meeting",
              tooltip:  m.hasPassed ? t("start_meeting_disabled_message_ended"): t("start_meeting_disabled_message"),
              disabled: !m.hasPassed || !m.isToday,
              hidden:   m.isActive,
              onClick:  () => meetingStore.startMeeting(m),
            },{
              type: "action",
              title: t("stop_meeting"),
              iconId: "dangerous",
              disabled: !m.canEdit,
              hidden: !m.isActive || !m.id,
              onClick: () => meetingStore.stopMeeting(m),
            },{
              type:     "action",
              title:    t("send_invitation"),
              tooltip:  t("send_invitation_disabled_message"),
              iconId:   "send",
              disabled: !m.canEdit || !m.canBeOpened,
              hidden:   !m.id,
              onClick:  async () => {
                try {
                  const count = await m.sendInvitations();
                  toast.success(`${count} st ${t("invitations_sent")}`);
                } catch (err) {
                  console.error("failed to send invitations", err);
                  toast.error(t("failed_invitation_sent"));
                }
              },
            },{
              type:     "action",
              title:    t("ics_export"),
              onClick:  m.icsExport,
              iconId:   "calendar",
              disabled: !m.canEdit
            },{
              type:      "action",
              title:     t("remove"),
              hidden:    !m.archived,
              iconId:    "delete",
              iconColor: theme.color.black,
              disabled:  !m.canEdit && !m.archived,
              onClick:   async () => {
                try {
                  await m.remove();
                  meetingStore.setActive(null);
                  toast.success(t("meeting_removed"));
                } catch (err) {
                  console.error("Failed to remove meeting", err);
                  toast.error(t("failed_remove_meeting"));
                }
              },
            },{
              type:     "action",
              title:    t("archive"),
              hidden:   m.archived,
              tooltip:  t("archive_meeting_disabled"),
              iconId:   "archive",
              disabled: !m.canEdit || m.isActive,
              onClick:  async () => {
                try {
                  await m.archive();
                  toast.success(t("meeting_archived"));
                } catch (err) {
                  console.error("Failed to archive meeting", err);
                  toast.error(t("failed_archive_meeting"));
                }
              },
            }
          ]
        }
      ]}
    />
  );
}));
